import { IAngularEvent, IDirective, IDirectiveFactory, ILocationService, IScope, ITimeoutService, IWindowService } from "angular";
import { StateService } from "@uirouter/angularjs";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { ProgressColoring } from "@gtmhub/configuration/services/progress-coloring";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { ApmSpanService } from "@gtmhub/core/tracing/apm-span.service";
import { IEmployee } from "@gtmhub/employees/models/models";
import { IIndicator } from "@gtmhub/error-handling";
import { filterActiveSessions } from "@gtmhub/sessions/redux/session-selectors";
import { offset } from "@gtmhub/shared/dom";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { ITeam } from "@gtmhub/teams";
import { IdMap } from "@gtmhub/util";
import { NavigationReportsCacheService } from "@webapp/navigation/services/navigation-reports-cache.service";
import { Session } from "@webapp/sessions/models/sessions.model";
import { IHomeTeam } from "../controllers/dashboard";
import { HomeService, IAssigneeStats, ISessionDelta, ISessionProgress } from "../services/home";
import { createEmployeeAndTeamMap } from "../utils";

const bindStateToScope = (state: IAssigneesStoreState) => ({
  reduxStatesInitialized: state.assignees.isFetched,
  reduxStateError: state.assignees.error,
  assigneesTypeUser: state.assignees.items.filter((assignee) => assignee.type === "user"),
});

export interface IHomeProgressScope extends IScope, IExtendedRootScopeService, ReturnType<typeof bindStateToScope> {
  employee: IEmployee;
  teams: IHomeTeam[];
  activeSessionsRedux: Session[];

  assigneesMap: IdMap<IEmployee | ITeam>;
  goalsAndMetricDeltasMap: IdMap<{ attainmentDelta: ISessionDelta }>;
  activeSessions: Session[];
  indicators: {
    loading: IIndicator;
  };
  hasReportingPermissions: boolean;
  sessionChangesDetected: boolean;
  sessionsBtnVisible: boolean;

  getSessionStatsCallback(): void;
  openSessionReport(session: Session): void;
  destroy(): void;
}

export interface IEnrichedSessionProgress extends Session {
  assigneeStats: IAssigneeStats[];
  sessionProgress: number;
  progressColor: string;
}

export class HomeProgress implements IDirective {
  public scope = {
    employee: "=",
    teams: "=",
    activeSessionsRedux: "<",
    destroy: "&",
  };
  public restrict = "";
  public template = require("../views/progress.html");

  constructor(
    private $state: StateService,
    private homeService: HomeService,
    private $window: IWindowService,
    private $rootScope: ITraceRootScopeService,
    private progressColoring: ProgressColoring,
    private $anchorScroll,
    private $location: ILocationService,
    private $ngRedux: INgRedux,
    private assigneeActions: AssigneeActions,
    private $timeout: ITimeoutService,
    private apmSpanService: ApmSpanService,
    private navigationReportsCache: NavigationReportsCacheService
  ) {}

  private $scope: IHomeProgressScope = null;

  private editions = {
    start: "gtmhub-start",
    startV2: "gtmhub-start-v2",
  };

  private states = {
    session: "gtmhub.goals.all",
    insightboard: "gtmhub.insightboard",
    reporting: "gtmhub.reporting",
  };

  private scroller: {
    currentPage: number;
    itemsPerPage: number;
    totalPages?: number;
    totalCount?: number;
  };
  private currentSessions: Session[];
  private currentSessionIds: string[];
  private employeeIdAndTeamIds: string;
  private homeScreen: HTMLElement;

  public link(scope: IHomeProgressScope): void {
    this.$scope = scope;

    this.apmSpanService.dataLoadSpan("progress-init", () => this.init());
  }

  private init = () => {
    this.$scope.indicators = {
      loading: { progress: true },
    };

    this.scroller = {
      currentPage: 1,
      itemsPerPage: 12,
    };
    this.$scope.activeSessions = [];
    this.$scope.goalsAndMetricDeltasMap = {};

    this.$scope.$on("accountSessionsUpdated", (event?: IAngularEvent, sessions?: Session[]) => {
      this.$scope.sessionChangesDetected = true;
      this.$scope.getSessionStatsCallback = () => {
        this.getActiveSessionStats(event, filterActiveSessions(sessions));
      };
    });

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);

    this.homeScreen = document.querySelector<HTMLElement>(".master-container");
    this.homeScreen.addEventListener("scroll", this.onScrollCallback);
    this.$scope.$on("$destroy", () => {
      this.homeScreen.removeEventListener("scroll", this.onScrollCallback);
      unsubscribe();
    });

    this.$scope.openSessionReport = this.openSessionReport;

    this.$scope.assigneesMap = createEmployeeAndTeamMap(this.$scope.employee, this.$scope.teams);
    this.currentSessions = this.$scope.activeSessionsRedux;

    this.$scope.hasReportingPermissions =
      (this.$rootScope.featureAvailable("reports") && this.$rootScope.hasPermission("ManageData")) || this.$rootScope.hasPermission("AccessReportsAndDataSourceFilters");

    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    return this.$rootScope.traceInit(() => {
      return this.$scope
        .watchUntilAndRejectOnError(
          () => this.$scope.reduxStatesInitialized,
          () => this.$scope.reduxStateError
        )
        .then(() => {
          if (this.shouldRemoveProgressComponent()) {
            this.$scope.destroy();
            return;
          }
          // we use the active sessions from redux on init
          // we do not want to use the updated sessions directly on every change
          // instead, when session changes occur, we allow the user to load the updated sessions by clicking on a button
          // which brings them back to the beginning of the section
          return this.getActiveSessionStats();
        });
    });
  };

  private onScrollCallback = () => {
    // the button should be visible when the scroll reaches the progress section
    const sessionsListEl = document.getElementById("sessions-list");
    this.$scope.sessionsBtnVisible = offset(sessionsListEl).top < this.homeScreen.clientHeight - 300;

    if (this.scroller.currentPage >= this.scroller.totalPages) {
      return;
    }

    // start loading when bottom of screen is reached
    // Issue on mac due to a small offset, which is also a float, requires adding 1px more for the calculation to be correct
    // https://stackoverflow.com/questions/9439725/how-to-detect-if-browser-window-is-scrolled-to-bottom
    if (this.homeScreen.scrollTop + 1 + this.homeScreen.clientHeight >= this.homeScreen.scrollHeight) {
      this.scroller.currentPage++;
      this.loadSomeStatsData(this.scroller);
    }
  };

  private openSessionReport = (session: Session): void => {
    if (this.$rootScope.editionUsed(this.editions.start) || this.$rootScope.editionUsed(this.editions.startV2)) {
      this.$state.go(this.states.reporting);
      return;
    }

    const performanceReport = this.navigationReportsCache.get().find((report) => (report.originalName || report.name).toLowerCase() === "performance report");
    const url = this.$state.href(this.states.insightboard, { dashboardId: performanceReport.id, sessionId: session.id });

    this.$window.open(url, "_blank");
  };

  private sortSessions = (sessions: Session[]): Session[] => {
    return sessions.sort((a, b) => {
      const aSessionPeriod = Date.parse(a.end) - Date.parse(a.start);
      const bSessionPeriod = Date.parse(b.end) - Date.parse(b.start);

      return aSessionPeriod - bSessionPeriod;
    });
  };

  private getActiveSessionStats = (event?: IAngularEvent, updatedSessions?: Session[]) => {
    if (this.shouldRemoveProgressComponent()) return;

    const activeTeams = this.$scope.employee.managedTeamIds
      .concat(this.$scope.employee.teamIds)
      .filter((teamId) => this.$scope.teams.some((team) => team.id === teamId && team.isActive !== false));

    this.employeeIdAndTeamIds = [this.$scope.employee.id]
      .concat(activeTeams)
      .filter((teamId, index, self) => self.indexOf(teamId) === index)
      .join(",");

    this.currentSessions = updatedSessions ? this.sortSessions(updatedSessions) : this.sortSessions(this.currentSessions);
    this.currentSessionIds = this.currentSessions.map((session) => session.id);
    this.scroller.totalCount = this.currentSessionIds.length;
    this.scroller.totalPages = Math.ceil(this.currentSessionIds.length / this.scroller.itemsPerPage);

    if (updatedSessions) {
      this.$scope.sessionChangesDetected = false;
      this.scroller.currentPage = 1;
      this.$location.hash("sessions-list");
      this.$anchorScroll();
    }

    return this.loadSomeStatsData(this.scroller);
  };

  private loadSomeStatsData = (scroller: { currentPage: number; itemsPerPage: number; totalPages?: number; totalCount?: number }) => {
    const start = (scroller.currentPage - 1) * scroller.itemsPerPage;
    const end = start + scroller.itemsPerPage;
    const sessionIds = this.currentSessionIds.slice(start, end).join(",");

    return this.homeService.getSessionsProgress(sessionIds, this.employeeIdAndTeamIds).then(
      (progress) => {
        delete this.$scope.indicators.loading;

        if (scroller.currentPage === 1) {
          this.$scope.activeSessions = this.enrichSessions(progress.items || [], this.currentSessions.slice(start, end) as IEnrichedSessionProgress[]);
        } else {
          this.$scope.activeSessions = this.$scope.activeSessions.concat(
            this.enrichSessions(progress.items || [], this.currentSessions.slice(start, end) as IEnrichedSessionProgress[])
          );
        }

        this.$timeout(() => {
          const hasVsScroll = this.homeScreen.scrollHeight !== this.homeScreen.clientHeight;

          if (!hasVsScroll && this.scroller.currentPage < this.scroller.totalPages) {
            this.scroller.currentPage++;
            this.loadSomeStatsData(this.scroller);
          }
        });
      },
      (error) => (this.$scope.indicators.loading = { error })
    );
  };

  private enrichSessions = (sessionsProgress: ISessionProgress[], sessions: IEnrichedSessionProgress[]) => {
    return sessions.map((session) => {
      const foundProgressSession = sessionsProgress.find((sProgress) => sProgress.sessionId === session.id);
      if (foundProgressSession) {
        this.$scope.goalsAndMetricDeltasMap[session.id] = { attainmentDelta: foundProgressSession.sessionAttainmentDelta };
        session.sessionProgress = foundProgressSession.sessionProgress;
        session.progressColor = this.progressColoring.getColorOrGeneric(this.homeService.roundProgressValue(session.sessionProgress));

        session.assigneeStats = foundProgressSession.assigneesStats
          ? foundProgressSession.assigneesStats.map((stat) => {
              this.$scope.goalsAndMetricDeltasMap[session.id][stat.assigneeId] = { attainmentDelta: stat.attainmentDelta };
              return { ...stat, color: this.progressColoring.getColorOrGeneric(this.homeService.roundProgressValue(stat.progress)) };
            })
          : [];

        session.assigneeStats.sort((a, b) => {
          if (
            a.assigneeId === this.$scope.employee.id ||
            (this.$scope.assigneesMap[a.assigneeId] as ITeam).name < (this.$scope.assigneesMap[b.assigneeId] as ITeam).name
          ) {
            return -1;
          }
          if (
            b.assigneeId === this.$scope.employee.id ||
            (this.$scope.assigneesMap[a.assigneeId] as ITeam).name > (this.$scope.assigneesMap[b.assigneeId] as ITeam).name
          ) {
            return 1;
          }
          return 0;
        });
      }
      return session;
    });
  };

  private shouldRemoveProgressComponent = () => this.$scope.assigneesTypeUser.length < 2;

  public static factory(): IDirectiveFactory {
    const directive = (
      $state: StateService,
      homeService: HomeService,
      $window: IWindowService,
      $rootScope: ITraceRootScopeService,
      progressColoring: ProgressColoring,
      $anchorScroll,
      $location: ILocationService,
      $ngRedux: INgRedux,
      assigneeActions: AssigneeActions,
      $timeout: ITimeoutService,
      apmSpanService: ApmSpanService,
      navigationReportsCache: NavigationReportsCacheService
    ) =>
      new HomeProgress(
        $state,
        homeService,
        $window,
        $rootScope,
        progressColoring,
        $anchorScroll,
        $location,
        $ngRedux,
        assigneeActions,
        $timeout,
        apmSpanService,
        navigationReportsCache
      );
    directive.$inject = [
      "$state",
      "HomeService",
      "$window",
      "$rootScope",
      "ProgressColoring",
      "$anchorScroll",
      "$location",
      "$ngRedux",
      "AssigneeActions",
      "$timeout",
      "ApmSpanService",
      "NavigationReportsCache",
    ];
    return directive;
  }
}
